import React, {FunctionComponent, useCallback, useEffect, useState, useRef} from 'react';
import {Button, Intent, MenuDivider, MenuItem, ButtonProps} from '@blueprintjs/core';
import {Select, ItemRenderer} from '@blueprintjs/select';
import {IconNames} from '@blueprintjs/icons';

import {DIVIDER, LOADING} from './constants';
import {IItem} from './types';
import {itemPredicate} from './utils';

type OnChangeCallback = (value: any) => void;

type CreateNewItemFromQueryCallback = (query: string) => IItem;
type CreateNewItemRendererCallback = (
  query: string,
  active: boolean,
  handleClick: React.MouseEventHandler<HTMLElement>
) => any;

interface IPopoverProps {
  usePortal: boolean;
  portalClassName?: string;
}

interface IInputProps {
  rightElement: any;
}

interface ISimpleSelectProps {
  selected?: string;
  disabled?: boolean;
  onQueryChange?: OnChangeCallback;
  onChange?: OnChangeCallback;
  options: IItem[];
  /*
   * By default, we handle the active item by ourselves.
   * This can be disabled by setting useActiveItem to false and let the Select component handle it.
   */
  useActiveItem?: boolean;
  createNewItemFromQuery?: CreateNewItemFromQueryCallback;
  createNewItemRenderer?: CreateNewItemRendererCallback;
  buttonProps?: ButtonProps;
  popoverProps?: IPopoverProps;
  inputProps?: IInputProps;
  selectDefault?: boolean;
  unselectDefault?: boolean;
  label?: string;
}

const ItemSelect = Select.ofType<IItem>();


const PopoverDefaultProps: IPopoverProps = {
  usePortal: false,
  portalClassName: '',
}

const InputDefaultProps: IInputProps = {
  rightElement: undefined
}

const createNewItemRenderer_ = (
  query: string,
  active: boolean,
  handleClick: React.MouseEventHandler<HTMLElement>,
) => (
  <MenuItem
    roleStructure="listoption"
    icon={IconNames.ADD}
    text={`Add "${query}"`}
    active={active}
    onClick={handleClick}
    shouldDismissPopover={false}
  />
);

const renderItem: ItemRenderer<IItem> = (item: IItem, {handleClick, modifiers}: any) => {
  if (item.label === DIVIDER.label) {
    return <MenuDivider key={DIVIDER.value}/>;
  } else if (item.label === LOADING.label) {
    return <MenuItem disabled={true} roleStructure="listoption" text="Loading..." key={LOADING.value}/>;
  } else if (!modifiers.matchesPredicate) {
    return null;
  }
  const hasIcon = !!item.icon || false;
  const isActive = modifiers.active;

  /*
   * If an MenuItem has icon specified and is active, show Intent.PRIMARY, else mark it with IconNames.TICK
   */
  return (
    <MenuItem
      roleStructure="listoption"
      icon={hasIcon ? item.icon : (isActive ? IconNames.TICK : undefined)}
      intent={(hasIcon && isActive) ? Intent.PRIMARY : undefined}
      text={item.label}
      key={item.value}
      label={item.secondary_label ? item.secondary_label : undefined}
      onClick={handleClick}/>
  );
};

interface DefaultSelectOption {
  label: string;
  value: string;
  icon?: any | null;
}

export const defaultSelectOption = (label: string): DefaultSelectOption[] => [
  {
    label: label,
    value: '',
    icon: ''
  }
];

const defaultSelectState = (select: boolean, options: IItem[], label: string) => {
  if (select && options.length > 0) {
    return options[0];
  }
  return defaultSelectOption(label)[0];
}

export const SimpleSelect: FunctionComponent<ISimpleSelectProps> = ({
  selected,
  disabled = true,
  onChange,
  options,
  useActiveItem = true,
  onQueryChange,
  createNewItemFromQuery = undefined,
  createNewItemRenderer = createNewItemRenderer_,
  buttonProps,
  popoverProps = PopoverDefaultProps,
  inputProps = InputDefaultProps,
  selectDefault = true,
  unselectDefault = false,
  label = 'No options available',
}: ISimpleSelectProps) => {
  const [select, setSelect] = useState(defaultSelectState(selectDefault, options, label)); // used only for rendering
  const previousSelect = useRef(select);

  useEffect(() => {
    const item = options.find(o => o.value === selected);
    if (item) {
      previousSelect.current = item;
      setSelect(item);
    } else if (!selectDefault) {
      setSelect(defaultSelectOption(label)[0]); // Reset select state if no matching item found
    }
  }, [selected, options]);

  const handleOnItemSelect = useCallback((value: any) => {
    /*
     * If the item is not selectable, do not change the selected item, but trigger the onChange event
     */
    if (value.selectable !== false) {
      setSelect(value);
    }

    if (previousSelect.current?.value !== value.value) {
      previousSelect.current = value;
      onChange && onChange(value);
    } else {
      if (unselectDefault) {
        onChange && onChange(defaultSelectOption(label)[0]);
      }
    }
  }, [onChange]);

  return (
    <ItemSelect
      disabled={disabled}
      activeItem={useActiveItem ? select : undefined}
      filterable={!!onQueryChange}
      items={options}
      itemRenderer={renderItem}
      onItemSelect={handleOnItemSelect}
      popoverProps={{
        captureDismiss: true /* close only this popover */,
        usePortal: popoverProps?.usePortal || false,
        minimal: true,
        portalClassName: popoverProps?.portalClassName || 'simple-select-portal',
      }}
      onQueryChange={onQueryChange ? onQueryChange : undefined}
      itemPredicate={onQueryChange ? itemPredicate : undefined}
      createNewItemFromQuery={createNewItemFromQuery}
      createNewItemRenderer={
        createNewItemFromQuery ? createNewItemRenderer : undefined
      }
      inputProps={{
        rightElement: inputProps?.rightElement || undefined,
      }}
    >
      <Button
        text={select?.label || ''}
        disabled={disabled}
        icon={select?.icon ? select.icon : undefined}
        rightIcon={IconNames.CARET_DOWN}
        minimal={true}
        fill={false}
        {...buttonProps}
      />
    </ItemSelect>
  );
}
